import { FlagId } from './../../models/communication.types';
import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import {
  Folder,
  FolderMessageContent,
  FoldersUnreadMessagesIndicator,
  FoldersUnreadMessagesIndicators,
  MessageInFolder,
} from '../../models/communication.types';
import { getFolders } from '../../services/communication/communication.service';
import store, { AppDispatch } from '../../store';

export interface MailboxState {
  activeFolder: Folder;
  messagesReceivedFolderUnreadMessages: number;
  specialCasesToSolveFolderUnreadMessages: number;
  helpDeskReportedFolderUnreadMessages: number;
  prevActiveMessageId: string | null;
  activeMessageId: string | null;
  replyMessageId: string | null;
  forwardMessageId: string | null;
  loadedMessageContent: FolderMessageContent | null;
  messagesToDelete: string[];
  messageToUpdateFlag: { messageId: string; flag: FlagId | null } | null;
  messagesToInsert: MessageInFolder[];
  conversationsToMove: string[];
  anyUnreadMessagesFound: boolean;
  isMenuMinimized: boolean;
}

const initialState: MailboxState = {
  activeFolder: Folder.Received,
  messagesReceivedFolderUnreadMessages: 0,
  specialCasesToSolveFolderUnreadMessages: 0,
  helpDeskReportedFolderUnreadMessages: 0,
  prevActiveMessageId: null,
  activeMessageId: null,
  replyMessageId: null,
  forwardMessageId: null,
  loadedMessageContent: null,
  messagesToDelete: [],
  messageToUpdateFlag: null,
  messagesToInsert: [],
  conversationsToMove: [],
  anyUnreadMessagesFound: false,
  isMenuMinimized: false,
};

const getFolderInfo = (
  folder: Folder,
  items: FoldersUnreadMessagesIndicator[],
): FoldersUnreadMessagesIndicator | undefined => {
  return items.find((item: FoldersUnreadMessagesIndicator) => item.folder === folder);
};

const getNewFoldersUnreadMessages = (
  state: MailboxState,
  messagesInFolder: MessageInFolder[],
  userId: string,
): Pick<
  MailboxState,
  | 'messagesReceivedFolderUnreadMessages'
  | 'specialCasesToSolveFolderUnreadMessages'
  | 'helpDeskReportedFolderUnreadMessages'
  | 'anyUnreadMessagesFound'
> => {
  let newReceivedMessagesCounter = 0;
  let newSpecialCasesToSolveMessagesCounter = 0;
  let newHelpdeskReportedMessagesCounter = 0;
  messagesInFolder.forEach((item) => {
    if (item.message.sender.userId !== userId) {
      switch (item.folder) {
        case Folder.Received:
          newReceivedMessagesCounter++;
          break;
        case Folder.SpecialCasesToSolve:
          newSpecialCasesToSolveMessagesCounter++;
          break;
        case Folder.HelpdeskReported:
          newHelpdeskReportedMessagesCounter++;
          break;
      }
    }
  });
  const messagesReceivedFolderUnreadMessages = state.messagesReceivedFolderUnreadMessages + newReceivedMessagesCounter;
  const specialCasesToSolveFolderUnreadMessages =
    state.specialCasesToSolveFolderUnreadMessages + newSpecialCasesToSolveMessagesCounter;
  const helpDeskReportedFolderUnreadMessages =
    state.helpDeskReportedFolderUnreadMessages + newHelpdeskReportedMessagesCounter;
  return {
    messagesReceivedFolderUnreadMessages,
    specialCasesToSolveFolderUnreadMessages,
    helpDeskReportedFolderUnreadMessages,
    anyUnreadMessagesFound:
      messagesReceivedFolderUnreadMessages +
        specialCasesToSolveFolderUnreadMessages +
        helpDeskReportedFolderUnreadMessages >
      0,
  };
};

const getUnReadItemsCounter = (folderUnreadMessagesIndicators: FoldersUnreadMessagesIndicator[]) =>
  folderUnreadMessagesIndicators.reduce(
    (result: number, nextFolderUnreadMessagesIndicator: FoldersUnreadMessagesIndicator) => {
      return result + nextFolderUnreadMessagesIndicator.unreadMessages;
    },
    0,
  );

const mailboxSlice = createSlice({
  name: 'mailbox',
  initialState,
  reducers: {
    updateFolders: (state, action: { type: string; payload: FoldersUnreadMessagesIndicator[] }) => {
      const folderReceivedItem = getFolderInfo(Folder.Received, action.payload);
      state.messagesReceivedFolderUnreadMessages = folderReceivedItem ? folderReceivedItem.unreadMessages : 0;

      const folderSpecialCasesToSolveItem = getFolderInfo(Folder.SpecialCasesToSolve, action.payload);
      state.specialCasesToSolveFolderUnreadMessages = folderSpecialCasesToSolveItem
        ? folderSpecialCasesToSolveItem.unreadMessages
        : 0;

      const folderHelpdeskReportedItem = getFolderInfo(Folder.HelpdeskReported, action.payload);
      state.helpDeskReportedFolderUnreadMessages = folderHelpdeskReportedItem
        ? folderHelpdeskReportedItem.unreadMessages
        : 0;
    },
    decreaseCurrentFolderUnreadMessages: (state, action: { type: string; payload: string }) => {
      const newState = { ...state };
      switch (state.activeFolder) {
        case Folder.Received:
          newState.messagesReceivedFolderUnreadMessages =
            newState.messagesReceivedFolderUnreadMessages > 0 ? newState.messagesReceivedFolderUnreadMessages - 1 : 0;
          break;
        case Folder.SpecialCasesToSolve:
          newState.specialCasesToSolveFolderUnreadMessages =
            newState.specialCasesToSolveFolderUnreadMessages > 0
              ? newState.specialCasesToSolveFolderUnreadMessages - 1
              : 0;
          break;
        case Folder.HelpdeskReported:
          newState.helpDeskReportedFolderUnreadMessages =
            newState.helpDeskReportedFolderUnreadMessages > 0 ? newState.helpDeskReportedFolderUnreadMessages - 1 : 0;
          break;
      }

      return { ...newState, ...getNewFoldersUnreadMessages(newState, [], action.payload) };
    },
    changeFolder: (state, action: { type: string; payload: Folder }) => {
      state.activeFolder = action.payload;
      state.prevActiveMessageId = null;
      state.activeMessageId = null;
      state.loadedMessageContent = null;
    },
    activateMessage: (state, action: { type: string; payload: string }) => {
      return {
        ...state,
        prevActiveMessageId: state.activeMessageId,
        activeMessageId: action.payload,
        loadedMessageContent: state.activeMessageId !== state.prevActiveMessageId ? null : state.loadedMessageContent,
      };
    },
    loadMessage: (state, action: { type: string; payload: FolderMessageContent }) => {
      state.loadedMessageContent = action.payload;
      state.activeMessageId = action.payload.messageId;
    },
    deActivateMessage: (state) => {
      state.prevActiveMessageId = state.activeMessageId;
      state.activeMessageId = null;
      state.loadedMessageContent = null;
    },
    addMessageToDelete: (state, action: { type: string; payload: string }) => {
      return { ...state, messagesToDelete: [...state.messagesToDelete, action.payload] };
    },
    removeMessagesToDelete: (state, action: { type: string; payload: string[] }) => {
      return { ...state, messagesToDelete: state.messagesToDelete.filter((item) => !action.payload.includes(item)) };
    },
    addConversationToMove: (state, action: { type: string; payload: string }) => {
      return { ...state, conversationsToMove: [...state.conversationsToMove, action.payload] };
    },
    removeConversationsToMove: (state, action: { type: string; payload: string[] }) => {
      return {
        ...state,
        conversationsToMove: state.conversationsToMove.filter((item) => !action.payload.includes(item)),
      };
    },
    openReplyForMessage: (state, action: { type: string; payload: string | null }) => {
      return {
        ...state,
        prevActiveMessageId: state.activeMessageId,
        replyMessageId: action.payload,
      };
    },
    closeReplyForMessage: (state) => {
      return {
        ...state,
        activeMessageId: state.prevActiveMessageId,
        replyMessageId: null,
      };
    },
    openForwardForMessage: (state, action: { type: string; payload: string | null }) => {
      return {
        ...state,
        prevActiveMessageId: state.activeMessageId,
        forwardMessageId: action.payload,
      };
    },
    closeForwardForMessage: (state) => {
      return {
        ...state,
        activeMessageId: state.prevActiveMessageId,
        forwardMessageId: null,
      };
    },
    addMessagesToInsert: (
      state,
      action: { type: string; payload: { messages: MessageInFolder[]; userId: string } },
    ) => {
      console.log('__________________________addMessagesToInsert:', action);
      console.log(
        '__________________________getNewFoldersUnreadMessages:',
        getNewFoldersUnreadMessages(state, action.payload.messages, action.payload.userId),
      );
      return {
        ...state,
        ...getNewFoldersUnreadMessages(state, action.payload.messages, action.payload.userId),
        messagesToInsert: [...state.messagesToInsert, ...action.payload.messages],
      };
    },
    setMessagesToInsertServed: (state, action: { type: string; payload: string[] }) => {
      console.log('__________________________setMessagesToInsertServed:', action);
      return {
        ...state,
        messagesToInsert: state.messagesToInsert.filter((item) => !action.payload.includes(item.message.messageId)),
      };
    },
    updateCurrentMessageFlag: (state, action: { type: string; payload: FlagId | null }) => {
      return {
        ...state,
        loadedMessageContent: state.loadedMessageContent
          ? { ...state.loadedMessageContent, flag: action.payload }
          : null,
      };
    },
    closeReplyingForwarding: (state) => {
      return {
        ...state,
        activeMessageId: state.replyMessageId !== null ? state.replyMessageId : state.forwardMessageId,
        replyMessageId: null,
        forwardMessageId: null,
      };
    },
    setMessageToUpdateFlag: (
      state,
      action: { type: string; payload: { messageId: string; flag: FlagId | null } | null },
    ) => {
      return { ...state, messageToUpdateFlag: action.payload };
    },
    updateAnyUnreadMessagesFound: (state, action: { type: string; payload: boolean }) => {
      return { ...state, anyUnreadMessagesFound: action.payload };
    },
    toggleIsMenuMinimized: (state, action: { type: string; payload: boolean | null }) => {
      return { ...state, isMenuMinimized: action?.payload ? action.payload : !state.isMenuMinimized };
    },
    setPrevMessageId: (state, action: { type: string; payload: string | null }) => {
      return { ...state, prevActiveMessageId: action?.payload };
    },
  },
});

export const loadFolders = () => {
  return (dispatch: AppDispatch) => {
    getFolders().then((response: AxiosResponse<FoldersUnreadMessagesIndicators>) => {
      dispatch(mailboxSlice.actions.updateFolders(response.data.folderUnreadMessagesIndicators));
    });
  };
};

const initAnyUnreadMessagesFound = () => {
  return (dispatch: AppDispatch) => {
    getFolders().then((response) => {
      const unReadItemsCounter = getUnReadItemsCounter(response.data.folderUnreadMessagesIndicators);
      dispatch(mailboxSlice.actions.updateAnyUnreadMessagesFound(unReadItemsCounter > 0));
    });
  };
};

export const mailboxActions = { ...mailboxSlice.actions, loadFolders, initAnyUnreadMessagesFound };
export default mailboxSlice;
